import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, X } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

// Use the bundled worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PdfViewerProps {
  url: string;
  onClose: () => void;
}

export default function PdfViewer({ url, onClose }: PdfViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load PDF document
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    pdfjsLib.getDocument(url).promise.then(
      (pdf) => {
        if (cancelled) return;
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        setCurrentPage(1);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        console.error('PDF load error:', err);
        setError('Impossible de charger le PDF');
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Render current page
  const renderPage = useCallback(async () => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas) return;

    // Cancel any in-progress render
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    try {
      const page = await pdf.getPage(currentPage);
      const viewport = page.getViewport({ scale });

      // Size canvas to fit container width if needed
      const container = containerRef.current;
      const containerWidth = container ? container.clientWidth : window.innerWidth;
      const fitScale = containerWidth / viewport.width;
      const finalScale = scale === 1 ? fitScale : scale;
      const finalViewport = page.getViewport({ scale: finalScale });

      canvas.width = finalViewport.width;
      canvas.height = finalViewport.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const renderTask = page.render({
        canvasContext: ctx,
        viewport: finalViewport,
        canvas,
      } as never);
      renderTaskRef.current = renderTask;

      await renderTask.promise;
      renderTaskRef.current = null;
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== 'RenderingCancelledException') {
        console.error('PDF render error:', err);
      }
    }
  }, [currentPage, scale]);

  useEffect(() => {
    if (!loading && numPages > 0) {
      renderPage();
    }
  }, [loading, numPages, currentPage, scale, renderPage]);

  const zoomIn = () => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)));
  const prevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const nextPage = () => setCurrentPage((p) => Math.min(numPages, p + 1));

  return (
    <div
      className="fixed inset-0 z-[100] bg-gray-100 flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-900 shrink-0 gap-2">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-white text-sm font-medium active:opacity-70 shrink-0"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Page navigation */}
        {numPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={prevPage}
              disabled={currentPage <= 1}
              className="w-8 h-8 flex items-center justify-center text-white bg-white/20 rounded-full active:bg-white/40 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-white text-xs font-medium min-w-[60px] text-center">
              {currentPage} / {numPages}
            </span>
            <button
              onClick={nextPage}
              disabled={currentPage >= numPages}
              className="w-8 h-8 flex items-center justify-center text-white bg-white/20 rounded-full active:bg-white/40 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Zoom controls */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={zoomOut}
            className="w-8 h-8 flex items-center justify-center text-white bg-white/20 rounded-full active:bg-white/40"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-white text-[10px] min-w-[32px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="w-8 h-8 flex items-center justify-center text-white bg-white/20 rounded-full active:bg-white/40"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* PDF content */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-200">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 p-8 text-center">
            <p className="text-gray-600 font-medium">{error}</p>
            <button onClick={onClose} className="text-primary-600 underline text-sm">
              Retour
            </button>
          </div>
        ) : (
          <div className="flex justify-center p-2 min-h-full">
            <canvas
              ref={canvasRef}
              className="shadow-lg bg-white"
              style={{ maxWidth: scale > 1 ? 'none' : '100%' }}
            />
          </div>
        )}
      </div>

      {/* Bottom bar with close */}
      <div
        className="shrink-0 p-3 bg-white border-t border-gray-200"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <button
          onClick={onClose}
          className="w-full py-3 bg-gray-900 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
        >
          <ChevronLeft className="w-4 h-4" />
          Fermer le document
        </button>
      </div>
    </div>
  );
}
